import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiAssistantService } from '../../services/aiAssistantService';
import Icon from '../AppIcon';

// Rutas públicas donde NUNCA debe aparecer el asistente, aunque exista un
// usuario en sesión (la landing y el login son superficies públicas).
const PUBLIC_ROUTES = new Set(['/', '/login', '/landing']);

const STORAGE_KEY = 'nova-ia-conv-id';
const POS_KEY = 'nova-ia-button-pos';
const IDLE_MS = 4000;
const BTN_SIZE = 56;
const DRAG_THRESHOLD = 5;

function loadPos() {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x === 'number' && typeof p?.y === 'number') return p;
  } catch {
    /* ignore */
  }
  return null;
}

function clampPos(x, y) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const margin = 8;
  return {
    x: Math.min(Math.max(margin, x), w - BTN_SIZE - margin),
    y: Math.min(Math.max(margin, y), h - BTN_SIZE - margin),
  };
}

function defaultPos() {
  return clampPos(
    window.innerWidth - BTN_SIZE - 20,
    window.innerHeight - BTN_SIZE - 20
  );
}

function isAdminRole(profile) {
  if (!profile) return false;
  if (profile.is_super_admin) return true;
  const r = (profile.role || '').toLowerCase();
  return r === 'admin' || r === 'superadmin';
}

const ChatWidget = () => {
  const { user, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [pdfAttached, setPdfAttached] = useState(null); // { filename, extracted }
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // Draggable + idle-fade state for the floating button
  const [pos, setPos] = useState(() => loadPos() || defaultPos());
  const [dragging, setDragging] = useState(false);
  const [idle, setIdle] = useState(false);
  const dragStateRef = useRef(null);
  const idleTimerRef = useRef(null);

  const allowed = isAdminRole(userProfile);
  const firstName = (userProfile?.full_name || user?.email || '')
    .trim()
    .split(/\s+/)[0]
    .split('@')[0];

  // Keep button inside viewport on resize
  useEffect(() => {
    const onResize = () => setPos(p => clampPos(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Idle fade: become semi-transparent after IDLE_MS without interaction
  const wakeButton = useCallback(() => {
    setIdle(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (!open) {
      idleTimerRef.current = setTimeout(() => setIdle(true), IDLE_MS);
    }
  }, [open]);

  useEffect(() => {
    wakeButton();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [wakeButton, pos.x, pos.y]);

  useEffect(() => {
    const toggleAssistant = () => {
      setOpen(current => !current);
      setIdle(false);
    };
    window.addEventListener('nova:assistant-toggle', toggleAssistant);
    return () =>
      window.removeEventListener('nova:assistant-toggle', toggleAssistant);
  }, []);

  // Drag handlers (pointer events cover mouse + touch)
  const onPointerDown = e => {
    if (open) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
    };
    wakeButton();
  };

  const onPointerMove = e => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    s.moved = true;
    setDragging(true);
    setPos(clampPos(s.origX + dx, s.origY + dy));
  };

  const onPointerUp = e => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (s.moved) {
      // Snap to nearest horizontal edge for cleanliness
      const w = window.innerWidth;
      const margin = 12;
      const snappedX =
        pos.x + BTN_SIZE / 2 < w / 2 ? margin : w - BTN_SIZE - margin;
      const finalPos = clampPos(snappedX, pos.y);
      setPos(finalPos);
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(finalPos));
      } catch {
        /* ignore */
      }
    } else {
      // Treat as click → toggle panel
      setOpen(v => !v);
    }
    dragStateRef.current = null;
    setDragging(false);
    wakeButton();
  };

  // Panel anchors to button position; flip side if button is on left
  const panelStyle = (() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const panelW = Math.min(390, w * 0.94);
    const panelH = Math.min(610, h - 64);
    return {
      right: Math.max(8, Math.min(16, w * 0.02)),
      top: 56,
      width: panelW,
      height: panelH,
    };
  })();

  const scrollToBottom = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  useEffect(() => {
    if (!open || !conversationId) return;
    let cancel = false;
    (async () => {
      setLoadingHistory(true);
      const r = await aiAssistantService.getMessages(conversationId);
      if (cancel) return;
      if (r.ok) {
        setMessages(r.data);
        scrollToBottom();
      } else {
        if (r.error?.includes('no encontrada')) {
          localStorage.removeItem(STORAGE_KEY);
          setConversationId(null);
          setMessages([]);
        }
      }
      setLoadingHistory(false);
    })();
    return () => {
      cancel = true;
    };
  }, [open, conversationId, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && !pdfAttached) || sending) return;
    setError(null);
    setSending(true);

    const messageToSend =
      text ||
      (pdfAttached
        ? `Adjunté la factura ${pdfAttached.filename}. Por favor revísala y prepárame el registro de la compra.`
        : '');
    // pdfContext incluye datos extraídos + storage para que el AI lo adjunte a la compra
    const pdfCtx = pdfAttached
      ? { ...pdfAttached.extracted, storage: pdfAttached.storage }
      : null;
    const fileLabel = pdfAttached?.filename || null;

    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: fileLabel ? `📎 ${fileLabel}\n${messageToSend}` : messageToSend,
      created_at: new Date().toISOString(),
    };
    setMessages(m => [...m, optimistic]);
    setInput('');
    setPdfAttached(null);
    scrollToBottom();

    const r = await aiAssistantService.sendMessage({
      message: messageToSend,
      conversationId,
      pdfContext: pdfCtx,
    });

    if (r.ok) {
      const newConvId = r.data.conversationId;
      if (newConvId && newConvId !== conversationId) {
        setConversationId(newConvId);
        localStorage.setItem(STORAGE_KEY, newConvId);
      }
      setMessages(m => [
        ...m,
        {
          id: `srv-${Date.now()}`,
          role: 'assistant',
          content: r.data.response || '(sin respuesta)',
          created_at: new Date().toISOString(),
        },
      ]);
      // Avisa a las páginas abiertas (Compras, Proveedores, Inventario) para que
      // recarguen su listado sin que el usuario tenga que hacer F5.
      const tools = Array.isArray(r.data.toolsCalled) ? r.data.toolsCalled : [];
      if (tools.length > 0) {
        window.dispatchEvent(
          new CustomEvent('nova:ai-data-changed', { detail: { tools } })
        );
      }
      scrollToBottom();
    } else {
      setError(r.error || 'Error desconocido');
      setMessages(m => [
        ...m,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${r.error || 'No pude procesar tu mensaje.'}`,
          created_at: new Date().toISOString(),
        },
      ]);
      scrollToBottom();
    }
    setSending(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setConversationId(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    setError(null);
    setPdfAttached(null);
  };

  const handlePickPdf = () => {
    fileInputRef.current?.click();
  };

  const handlePdfChange = async e => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!f) return;
    const accepted = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
    ];
    if (!accepted.includes(f.type)) {
      setError('Solo se aceptan archivos PDF o imágenes (JPG, PNG, WebP).');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('El archivo excede 10 MB.');
      return;
    }
    setError(null);
    setUploadingPdf(true);
    const r = await aiAssistantService.uploadPdf(f);
    setUploadingPdf(false);
    if (r.ok) {
      setPdfAttached({
        filename: r.data.filename,
        extracted: r.data.extracted,
        storage: r.data.storage || null,
      });
    } else {
      setError(r.error || 'No pude procesar el archivo.');
    }
  };

  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.has(location.pathname);
  if (isPublicRoute || !user || !allowed) return null;

  return (
    <>
      {/* Floating draggable button */}
      <button
        type="button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={wakeButton}
        onFocus={wakeButton}
        className="hidden"
        style={{
          left: pos.x,
          top: pos.y,
          width: BTN_SIZE,
          height: BTN_SIZE,
          backgroundColor: 'var(--brand, #2563eb)',
          opacity: open ? 1 : idle && !dragging ? 0.35 : 1,
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          transition:
            'opacity 400ms ease, transform 150ms ease' +
            (dragging ? '' : ', left 200ms ease, top 200ms ease'),
          userSelect: 'none',
        }}
        aria-label={open ? 'Cerrar Alex AI' : 'Abrir Alex AI'}
        title="Alex AI (arrastra para mover)"
      >
        <Icon name={open ? 'X' : 'Bot'} size={24} />
      </button>

      {/* Panel anchored near button */}
      {open && (
        <div
          className="fixed z-[120] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          style={panelStyle}
        >
          <header
            className="px-4 py-3 flex items-center justify-between text-white"
            style={{ backgroundColor: 'var(--brand, #2563eb)' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Icon name="Bot" size={16} />
              </div>
              <div>
                <div className="font-semibold text-sm leading-tight">
                  Alex AI
                </div>
                <div className="text-[11px] opacity-80 leading-tight">
                  Asistente operativo
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleNewChat}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                title="Nueva conversación"
              >
                <Icon name="Plus" size={16} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                title="Cerrar"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50"
          >
            {loadingHistory && (
              <div className="text-xs text-gray-500 text-center py-3">
                Cargando…
              </div>
            )}
            {!loadingHistory && messages.length === 0 && (
              <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-3">
                <p className="font-medium text-gray-900 mb-2">
                  {firstName ? `¡Hola, ${firstName}!` : '¡Hola!'} ¿En qué te
                  ayudo?
                </p>
                <p className="text-xs mb-2">
                  Esto es lo que puedo hacer por ti:
                </p>
                <div className="text-xs space-y-2 text-gray-700">
                  <div>
                    <p className="font-medium text-gray-800">
                      Obras y reportes
                    </p>
                    <ul className="list-disc list-inside ml-1 text-gray-600">
                      <li>Listar obras y ver el estado financiero de una</li>
                      <li>
                        Reporte consolidado multi-obra (presupuesto,
                        comprometido, pagado, facturado, utilidad)
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      Compras y proveedores
                    </p>
                    <ul className="list-disc list-inside ml-1 text-gray-600">
                      <li>
                        Buscar compras por obra/proveedor/folio/fecha y ver su
                        detalle
                      </li>
                      <li>
                        Registrar compras nuevas (con PDF o foto de factura)
                      </li>
                      <li>
                        Buscar/crear proveedores y ver su estado de cuenta
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      Inventario y almacenes
                    </p>
                    <ul className="list-disc list-inside ml-1 text-gray-600">
                      <li>Buscar/crear items y consultar stock por almacén</li>
                      <li>
                        Kardex de movimientos (entradas, salidas, traspasos,
                        consumos)
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      Tesorería y gastos
                    </p>
                    <ul className="list-disc list-inside ml-1 text-gray-600">
                      <li>Saldos de cajas y cuentas bancarias</li>
                      <li>
                        Listar gastos por obra/proveedor/fecha y saldo por pagar
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      Asistencia y nómina
                    </p>
                    <ul className="list-disc list-inside ml-1 text-gray-600">
                      <li>Consultar asistencia por empleado, sitio o fechas</li>
                      <li>
                        Consultar nómina semanal (horas, extras, neto a pagar)
                      </li>
                    </ul>
                  </div>
                </div>
                <p className="text-[11px] mt-2 text-gray-500">
                  Pagos, cancelaciones y movimientos manuales de inventario aún
                  no están habilitados como acción; pídelos como consulta.
                </p>
              </div>
            )}
            {messages.map(m => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-gray-500 px-2">
                <span className="inline-block w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
                Pensando…
              </div>
            )}
          </div>

          {error && (
            <div className="px-4 py-2 bg-red-50 text-red-700 text-xs border-t border-red-200">
              {error}
            </div>
          )}

          <div className="border-t border-gray-200 p-3 bg-white">
            {pdfAttached && (
              <div className="mb-2 flex items-center justify-between gap-2 px-2 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-900">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon name="FileText" size={14} />
                  <span className="truncate">
                    {pdfAttached.filename}
                    {pdfAttached.extracted?.total != null && (
                      <span className="ml-1 opacity-75">
                        · $
                        {Number(pdfAttached.extracted.total).toLocaleString(
                          'es-MX',
                          { minimumFractionDigits: 2 }
                        )}
                      </span>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPdfAttached(null)}
                  className="p-0.5 hover:bg-purple-100 rounded"
                  title="Quitar archivo"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            {uploadingPdf && (
              <div className="mb-2 text-xs text-gray-500 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
                Procesando archivo…
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={handlePdfChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={handlePickPdf}
                disabled={sending || uploadingPdf}
                className="px-2 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Adjuntar factura (PDF o imagen)"
              >
                <Icon name="Paperclip" size={16} />
              </button>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={
                  pdfAttached
                    ? 'Indicaciones extra (opcional)…'
                    : 'Escribe tu mensaje… (Enter para enviar)'
                }
                className="flex-1 resize-none px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand,#2563eb)] focus:border-transparent max-h-32"
                disabled={sending}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || (!input.trim() && !pdfAttached)}
                className="px-3 py-2 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                style={{ backgroundColor: 'var(--brand, #2563eb)' }}
                title="Enviar"
              >
                <Icon name="Send" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          isUser
            ? 'text-white rounded-br-sm'
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
        }`}
        style={
          isUser ? { backgroundColor: 'var(--brand, #2563eb)' } : undefined
        }
      >
        {message.content}
      </div>
    </div>
  );
};

export default ChatWidget;
